import React, { useEffect, useRef } from 'react';
import { X, Check, Edit2, Trash2, Bot, User, Copy, CheckCheck, AlertTriangle } from 'lucide-react';
import { useLlmAgent } from './LlmAgentContext';
import { parseChangePreview, extractProseWithoutTool, parseToolInvocation } from './previewParser';
import './AiConversationDrawer.css';

interface Props {
  onEdit?: (content: string) => void;
  onClose?: () => void;
}

export function AiConversationDrawer({ onEdit, onClose }: Props) {
  const { sessionMessages, isConvoOpen, setIsConvoOpen, clearSession, accept } = useLlmAgent();
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isConvoOpen) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isConvoOpen, sessionMessages]);

  if (!isConvoOpen) return null;

  const handleClose = () => {
    setIsConvoOpen(false);
    onClose?.();
  };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="ai-convo-overlay" onClick={handleClose}>
      <div className="ai-convo-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="AI Session Conversation">
        <div className="ai-convo-header">
          <div className="ai-convo-header__title">
            <Bot size={18} className="ai-convo-header__icon" />
            <span>AI Session Conversation</span>
            <span className="ai-convo-header__badge">{sessionMessages.length} message{sessionMessages.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="ai-convo-header__actions">
            {sessionMessages.length > 0 && (
              <button
                className="ai-convo-btn ai-convo-btn--ghost"
                onClick={clearSession}
                title="Clear conversation history"
                aria-label="Clear conversation history"
              >
                <Trash2 size={15} />
                <span>Clear</span>
              </button>
            )}
            <button
              className="ai-convo-btn ai-convo-btn--icon"
              onClick={handleClose}
              aria-label="Close conversation drawer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ai-convo-body">
          {sessionMessages.length === 0 ? (
            <div className="ai-convo-empty">
              <Bot size={32} className="ai-convo-empty__icon" />
              <p className="ai-convo-empty__title">No conversation in this session yet</p>
              <p className="ai-convo-empty__sub">
                Run an AI command from the bottom bar (e.g. <code>ai suggest</code>, <code>ai plan week</code>, <code>ai insights</code>) to view the stream here.
              </p>
            </div>
          ) : (
            <div className="ai-convo-messages">
              {sessionMessages.map((msg) => (
                <div key={msg.id} className={`ai-convo-msg ai-convo-msg--${msg.role}`}>
                  <div className="ai-convo-msg__header">
                    <div className="ai-convo-msg__author">
                      {msg.role === 'user' ? (
                        <>
                          <User size={14} />
                          <span>You</span>
                        </>
                      ) : msg.role === 'assistant' ? (
                        <>
                          <Bot size={14} />
                          <span>AI Assistant</span>
                        </>
                      ) : (
                        <span>System</span>
                      )}
                    </div>
                    <div className="ai-convo-msg__meta">
                      <span className="ai-convo-msg__time">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {msg.role === 'assistant' && (
                        <button
                          className="ai-convo-msg__action-btn"
                          onClick={() => handleCopy(msg.id, msg.content)}
                          title="Copy response"
                          aria-label="Copy response"
                        >
                          {copiedId === msg.id ? <CheckCheck size={13} color="var(--success, #10b981)" /> : <Copy size={13} />}
                        </button>
                      )}
                    </div>
                  </div>
                  {msg.role === 'assistant' && (() => {
                    const preview = parseChangePreview(msg.content, msg.appId);
                    if (!preview || msg.kind !== 'action') return null;
                    return (
                      <div className="ai-convo-msg__preview">
                        <div className="ai-convo-msg__preview-header">
                          <span className="ai-convo-msg__preview-title">{String(preview.title ?? '')}</span>
                          <span className={`ai-convo-msg__preview-badge ${preview.destructiveNotice ? 'ai-convo-msg__preview-badge--destructive' : ''}`}>
                            {String(preview.actionType ?? '')}
                          </span>
                        </div>
                        {preview.items.length > 0 && (
                          <ul className="ai-convo-msg__preview-items">
                            {preview.items.map((item, idx) => (
                              <li key={idx}>
                                <strong>{String(item.label)}:</strong> {typeof item.detail === 'object' ? JSON.stringify(item.detail) : String(item.detail ?? '')}
                              </li>
                            ))}
                          </ul>
                        )}
                        {preview.destructiveNotice && (
                          <div className="ai-convo-msg__preview-notice">
                            <AlertTriangle size={11} />
                            <span>{String(preview.destructiveNotice)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    const contentStr = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2);
                    const isTool = msg.role === 'assistant' && (parseToolInvocation(contentStr) !== null || parseChangePreview(contentStr, msg.appId)?.actionType === 'tool');
                    const prose = isTool ? extractProseWithoutTool(contentStr) : contentStr;
                    if (!prose.trim()) return null;
                    return (
                      <div className="ai-convo-msg__content">
                        {prose}
                      </div>
                    );
                  })()}
                  {msg.kind === 'action' && msg.status && <p role="status">{msg.status === 'pending' ? 'Awaiting confirmation' : msg.status === 'running' ? 'Executing' : msg.status === 'success' ? 'Applied locally' : msg.status === 'cancelled' ? 'Cancelled' : 'Failed'}</p>}
                  {msg.role === 'assistant' && (
                    <div className="ai-convo-msg__actions">
                      {msg.status === 'pending' && msg.kind === 'action' && msg.onAccept && (
                        <button
                          className="ai-convo-btn ai-convo-btn--primary ai-convo-btn--sm"
                          onClick={() => { void accept(msg.content, msg.id).catch(() => {}); }}
                        >
                          <Check size={13} /> Confirm &amp; execute
                        </button>
                      )}
                      {onEdit && (() => {
                        const contentStr = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2);
                        const isTool = parseToolInvocation(contentStr) !== null || parseChangePreview(contentStr, msg.appId)?.actionType === 'tool';
                        const prose = isTool ? extractProseWithoutTool(contentStr) : contentStr;
                        if (!prose.trim()) return null;
                        return (
                          <button
                            className="ai-convo-btn ai-convo-btn--sm"
                            onClick={() => {
                              onEdit(prose);
                              handleClose();
                            }}
                          >
                            <Edit2 size={13} /> Edit in Bar
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
